import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useDebounce, useToast } from "../../../../shared/hooks";
import { FinancialService } from "../../../../shared/services/api/financial/FinancialService";
import { useBatchGenerateInvoiceByRow } from "../../hooks/useBatchGenerateInvoiceByRow";
import { GenericToolbar } from "../GenericToolbar";
import { ModalConfirmSelect } from "../Modals/ModalConfirmSelect";
import { TableInstallmentBillet } from "../TableInstallmentBillet";

export const BilletCardTab = () => {
	const { debounce } = useDebounce();
	const toastHandler = useToast();
	const [sortField, setSortField] = useState<"name" | "value" | "count" | "due_date">("value");
	const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

	const [textSearch, setTextSearch] = useState("");
	const [dataIni, setDataIni] = useState("");
	const [dataFim, setDataFim] = useState("");
	const [status, setStatus] = useState("pending");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const [billingData, setBillingData] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	const handleFetchData = (
		currentPage: number,
		limit: number,
		search: string,
		ini: string,
		fim: string,
		stat: string,
		currentSortField: "name" | "value" | "count" | "due_date",
		currentSortOrder: "ASC" | "DESC"
	) => {
		setIsLoading(true);
		debounce(() => {
			FinancialService.getInstallmentBillet(currentPage + 1, limit, search, stat, ini, fim, "billet", currentSortField, currentSortOrder)
				.then((response) => {
					if (response instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title="Erro" content="Erro ao buscar valores de faturamento" />,
						});
					} else {
						setBillingData(response.data);
						setTotalCount(response.totalCount ?? 0);
					}
				})
				.finally(() => setIsLoading(false));
		});
	};

	useEffect(() => {
		const defaultDataIni = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0];
		const defaultDataFim = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split("T")[0];

		setTextSearch("");
		setDataIni(defaultDataIni);
		setDataFim(defaultDataFim);
		setStatus("pending");

		handleFetchData(0, rowsPerPage, "", defaultDataIni, defaultDataFim, "pending", sortField, sortOrder);
	}, []);

	useEffect(() => {
		if (!dataIni || !dataFim) return;

		handleFetchData(page, rowsPerPage, textSearch, dataIni, dataFim, status, sortField, sortOrder);
	}, [refreshTrigger, page, rowsPerPage, sortField, sortOrder]);

	const handleChangePage = (newPage: number) => setPage(newPage);
	const handleChangeRowsPerPage = (newRowsPerPage: number) => {
		setRowsPerPage(newRowsPerPage);
		setPage(0);
	};
	const handleRefresh = () => setRefreshTrigger((prev) => prev + 1);

	const handleSearch = () => {
		setPage(0);
		handleFetchData(0, rowsPerPage, textSearch, dataIni, dataFim, status, sortField, sortOrder);
	};

	const handleSortChange = (field: "name" | "value" | "count" | "due_date") => {
		const nextOrder = sortField === field && sortOrder === "ASC" ? "DESC" : "ASC";
		setPage(0);
		setSortField(field);
		setSortOrder(nextOrder);
	};

	const toggleSelect = (clientId: string) => {
		setSelectedClientIds((prev) => (prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]));
	};

	const toggleSelectAll = () => {
		if (selectedClientIds.length === billingData.length) {
			setSelectedClientIds([]);
		} else {
			setSelectedClientIds(billingData.map((r) => r.clientId));
		}
	};

	const { handleBatchGenerateInvoice } = useBatchGenerateInvoiceByRow({
		setIsLoading,
		onSuccess: handleRefresh,
		dataIni,
		dataFim,
	});

	const handleConfirmGenerateInvoice = () => {
		const selectedRows = billingData
			.filter((row) => selectedClientIds.includes(row.clientId))
			.map((row) => ({
				clientId: row.clientId,
				name: row.name,
				sum_amount: row.sum_amount,
				count_amount: row.count_amount,
				day_due_date: row.day_due_date,
			}));

		handleBatchGenerateInvoice(selectedRows);
	};

	return (
		<Box>
			<GenericToolbar
				showInputSearch
				showDateRange
				showStatusSelect={false}
				textSearch={textSearch}
				onChangeTextSearch={(value: string) => setTextSearch(value)}
				onClearTextSearch={() => setTextSearch("")}
				onChangeDateStart={(date: string) => setDataIni(date)}
				onChangeDateEnd={(date: string) => setDataFim(date)}
				onChangeStatus={(statusValue: string) => setStatus(statusValue)}
				onClickRefresh={handleSearch}
				showGenerateInvoiceButton={selectedClientIds.length > 0}
				onClickGenerateInvoice={() => setIsConfirmModalOpen(true)}
			/>

			<LoadingComp isopen={isLoading} />
			<Box sx={{ overflowX: "auto" }}>
				<TableInstallmentBillet
					rows={billingData}
					page={page}
					rowsPerPage={rowsPerPage}
					totalCount={totalCount}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					dataIni={dataIni}
					dataFim={dataFim}
					onRefresh={handleRefresh}
					sortField={sortField}
					sortOrder={sortOrder}
					onSortChange={handleSortChange}
					onToggleSelectAll={toggleSelectAll}
					selectedClientIds={selectedClientIds}
					onToggleSelect={toggleSelect}
				/>
			</Box>

			<ModalConfirmSelect
				open={isConfirmModalOpen}
				onClose={() => setIsConfirmModalOpen(false)}
				onConfirm={() => {
					setIsConfirmModalOpen(false);
					handleConfirmGenerateInvoice();
				}}
				title="Deseja gerar as faturas selecionadas?"
				description="As faturas serão geradas com base nos dados visíveis da tabela, como data de vencimento, valor total e quantidade de garantias."
				customContent={
					<Box>
						<Typography variant="subtitle1">Essa ação não poderá ser desfeita.</Typography>
						<br />
						<Typography variant="subtitle2">Verifique se os dados estão corretos antes de confirmar o faturamento.</Typography>
					</Box>
				}
				confirmLabel="Sim, gerar faturas"
				cancelLabel="Cancelar"
			/>
		</Box>
	);
};
